// React hooks
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// Helpers i API servisi
import { deleteContactAddressAPI, getContactAddressesAPI, getContactAPI } from "services/api/contacts/api";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi i konstante
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  ContactAddressSearchPageCmp,
  ContactAddressSearchPageProps,
} from "./types";
import { CONTACT_ADDRESS_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";

// Moduli specifični za projekt
import {
  Routes,
} from "ui/modules/contacts/navigation";
import TabMenu from "ui/modules/contacts/pages/components/TabMenu/TabMenu";

// Lokalne komponente
import ContactAddressSearchResults from "./components/ContactAddressSearchResults/ContactAddressSearchResults";
import ContactAddressSearchFilters from "./components/ContactAddressSearchFilters/ContactAddressSearchFilters";

import ContactAddressEditCard from "../ContactAddressEditCard/ContactAddressEditCard";
import { ContactAddressRow, ContactAddressSearchFormValues, ContactDetailFormValues } from "services/api/contacts/types";
import { Dialog } from "ui/components/Dialog";
import { useTranslate } from "helpers";
import { BreadcrumbItem } from "ui/components/Breadcrumb/types";
import { SIDEBAR_ITEM_CONTACT } from "app/components/Sidebar/consts";
import { initialContactDetailForm } from "services/api/contacts/consts";

const ContactAddressSearchPage: ContactAddressSearchPageCmp = (
  props: ContactAddressSearchPageProps
) => {
  const { id } = useParams();

  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<ContactAddressRow>
  >(initialDataWithPagination);

  const [formValues, setFormValues] = useState<ContactDetailFormValues>(
    initialContactDetailForm
  );

  const [addressId, setAddressId] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);



  useEffect(() => {
    if (id) {
      fetchSearchResult(searchResults.pagination, id);
      fetchContact(id);
    }
  }, [id]);

  const fetchContact = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialContactDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getContactAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);
  };

  const fetchSearchResult = async (pagination: Pagination, contactId: string, name?: string | null) => {
    setIsLoading(true);
    try {
      if (id) {
        const results = await getContactAddressesAPI(pagination, contactId, name);

        setSearchResults(results);
      }
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    setAddressId(id);
    setShowAddNewModal(true);
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAddressId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback((searchFormValues: ContactAddressSearchFormValues) => {
    if (id)
      fetchSearchResult(searchResults.pagination, id, searchFormValues.search);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    if (id)
      fetchSearchResult(newPagination, id);
  };

  const handleEditSuccess = useCallback(() => {
    if (id)
      fetchSearchResult(searchResults.pagination, id);
    setShowAddNewModal(false);
  }, []);

  const handleDeleteClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setAddressId(id);
    setOpenDialog(true);
  }

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!addressId) return;

    try {
      if (id) {
        const deleted = await deleteContactAddressAPI(
          t,
          id,
          addressId,
        );

        if (deleted) {
          await fetchSearchResult(searchResults.pagination, id);
        }
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setAddressId("");
    } catch (err) {
      console.error("Greška prilikom brisanja adrese:", err);
    }
  }, [addressId, t, id, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setAddressId("");
    setOpenDialog(false);
  }, []);

  const handleGoogleMapClick = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement>,
      latitude: number | null,
      longitude: number | null
    ) => {
      event.stopPropagation();
      event.preventDefault();
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
      window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
    },
    []
  );

  const handleWhatsAppClick = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement>,
      address: string,
      latitude: number | null,
      longitude: number | null
    ) => {
      event.stopPropagation();
      if (latitude === null || longitude === null) return;

      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      const message = `${address}\n${googleMapsUrl}`;
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, "_blank");
    },
    []
  );

  const handleViberClick = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement>,
      address: string,
      latitude: number | null,
      longitude: number | null
    ) => {
      event.stopPropagation();
      if (latitude === null || longitude === null) return;

      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      const message = `${address}\n${googleMapsUrl}`;
      const viberUrl = `viber://forward?text=${encodeURIComponent(message)}`;
      window.open(viberUrl, "_blank");
    },
    []
  );

  const handleCopyLinkClick = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement>,
      latitude: number | null,
      longitude: number | null
    ) => {
      event.stopPropagation();
      if (latitude === null || longitude === null) return;

      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      navigator.clipboard.writeText(googleMapsUrl).then(() => {
        // Možeš dodati toast notification ovdje
        alert("Link kopiran!");
      });
    },
    []
  );

  const breadcrumbs: BreadcrumbItem[] = [
    { text: SIDEBAR_ITEM_CONTACT.text, route: SIDEBAR_ITEM_CONTACT.route },
  ];


  if (!id) return <>REDIRECT TODO</>;

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <div className="flex justify-between">
              <div className="flex flex-col gap-2">
                <div className="flex gap-3">
                  {formValues.pictures && formValues.pictures.length > 0 && (
                    <img src={formValues.pictures[0].url} alt={formValues.companyName ?? ""} title={formValues.companyName ?? ""} className="h-12 w-12 object-contain rounded-full" />
                  )}
                  <div>
                    <PageTitle
                      title={`${t(CONTACT_ADDRESS_SEARCH_PAGE_TITLE.text)} [${formValues.companyName || `${formValues.firstName} ${formValues.lastName}`}]`}
                      icon={CONTACT_ADDRESS_SEARCH_PAGE_TITLE.icon}
                      iconColor={CONTACT_ADDRESS_SEARCH_PAGE_TITLE.iconColor}
                    />
                    <Breadcrumb items={breadcrumbs} />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 items-center">
              </div>
            </div>
          </PageHeader>
          <TabMenu contactId={id} contactTypeId={formValues?.contactType?.id} />
        </div>
        <div className="px-4 py-4">
          <ContactAddressSearchFilters
            onAddNew={handleAddNew}
            onSearchChange={handleSearchChange}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Unesite telefon klikom na 'Dodaj telefon'" />
          ) : (
            <ContactAddressSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onGoogleMapClick={handleGoogleMapClick}
              onWhatsAppClick={handleWhatsAppClick}
              onViberClick={handleViberClick}
              onCopyLinkClick={handleCopyLinkClick}
              onDeleteClick={handleDeleteClick}
            />
          )}
        </div>
      </div>
      <ContactAddressEditCard
        addressId={addressId}
        contactId={id}
        show={showAddNewModal}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <Dialog
        open={openDialog}
        title={t(
          "admin.contact.address.dialog.delete.title"
        )}
        message={t(
          "admin.contact.address.dialog.delete.message"
        )}
        confirmText={t(
          "admin.contact.address.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.contact.address.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
};

ContactAddressSearchPage.route = Routes.ContactAddressSearchPageRouter;

export default ContactAddressSearchPage;
